// userNotificationService.tsx
// Service for handling user notification API calls (fetches notification history from backend API)
// This is separate from Firebase NotificationService which handles push notifications
import axios from "axios";
import logger, { Logger } from "../utils/logger";

// Base API configuration
const API_ENDPOINT = process.env.REACT_APP_API_URL || "https://mapi2.3sigmacrm.com/api/v1";

// Header options for API requests
const getHeaderOptions = {
  "Content-Type": "application/json",
  "Accept": "application/json",
};

// 🔔 NOTIFICATION TYPE DEFINITIONS
export interface NotificationData {
  _id: string;
  user: string;
  organization: string;
  deviceTokens: string[];
  notificationType: "new_lead" | "task_reminder_summary" | "repeat_task_due_reminder" | "weekly_summary" | string;
  leadSource: string | null;
  title: string;
  body: string;
  messageData: {
    toScreen: "LEAD_PROFILE_SCREEN" | "TASK_HOME_SCREEN" | string;
    reloadleads?: boolean;
    leadId?: string;
  } | null;
  fcmResult: any;
  error: string | null;
  extraData: any;
  createdAt: string;
}

export interface NotificationResponse {
  statusCode: number;
  status: boolean;
  data: {
    data: NotificationData[];
    totalCount?: number;
    currentPage?: number;
    totalPages?: number;
  };
}

// 📡 API FUNCTIONS

/**
 * Fetch notifications for the current user
 * @param page - Page number (default: 1)
 * @param perPage - Number of notifications per page (default: 15)
 * @returns Promise with notification response
 */
export const getNotifications = async (
  page: number = 1, 
  perPage: number = 15
): Promise<NotificationResponse> => {
  try {
    Logger.log(`🔔 Fetching notifications - Page: ${page}, PerPage: ${perPage}`);
    
    // Prepare headers with auth token
    const headers = {
      ...getHeaderOptions,
      Authorization: localStorage.getItem("auth_token") || "",
    };
    
    // Make API call to notification endpoint
    const response = await axios.get(
      `${API_ENDPOINT}/user/notification?page=${page}&perPage=${perPage}`,
      { headers }
    );
    
    Logger.log('🔔 Notification API Response:', response.data);
    
    // Validate response structure
    if (!response.data || typeof response.data.status !== 'boolean') {
      throw new Error('Invalid response structure from notification API');
    }
    
    return response.data;
  } catch (error) {
    Logger.log('❌ Error fetching notifications:', error);
    
    // Handle different error scenarios
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      if (status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (status === 403) {
        throw new Error('Access denied. Insufficient permissions.');
      } else if (status >= 500) {
        throw new Error('Server error. Please try again later.');
      }
    }
    
    throw new Error('Failed to fetch notifications. Please check your connection.');
  }
};

/**
 * Get notification icon based on notification type
 * @param notificationType - Type of notification
 * @returns Icon name and color for UI
 */
export const getNotificationIcon = (notificationType: string): { icon: string; color: string } => {
  logger.log('🎨 Getting icon for notification type:', notificationType);
  
  switch (notificationType) {
    case 'new_lead':
      return { icon: 'Bell', color: '#28a745' }; // Green for new leads
    case 'task_reminder_summary':
      return { icon: 'Calendar', color: '#fd7e14' }; // Orange for task reminders
    case 'repeat_task_due_reminder':
      return { icon: 'Clock', color: '#dc3545' }; // Red for overdue tasks
    case 'weekly_summary':
      return { icon: 'BarChart3', color: '#007bff' }; // Blue for reports
    default:
      return { icon: 'Bell', color: '#6c757d' }; // Gray for unknown types
  }
};

/**
 * Format notification time to human readable format
 * @param createdAt - ISO date string
 * @returns Formatted time string (e.g., "2m ago", "1d ago")
 */
export const formatNotificationTime = (createdAt: string): string => {
  try {
    logger.log('⏰ Formatting time for:', createdAt);
    
    const now = new Date();
    const notificationTime = new Date(createdAt);
    const diffInSeconds = Math.floor((now.getTime() - notificationTime.getTime()) / 1000);
    
    // Handle invalid dates
    if (isNaN(diffInSeconds)) {
      return 'Unknown time';
    }
    
    // Format based on time difference
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months}mo ago`;
    }
  } catch (error) {
    Logger.log('❌ Error formatting notification time:', error);
    return 'Unknown time';
  }
};

/**
 * Handle notification click navigation
 * @param notification - Notification data object
 * @param navigate - React Router navigate function
 * @returns void
 */
export const handleNotificationClick = (
  notification: NotificationData,
  navigate: (path: string, options?: any) => void
): void => {
  try {
    logger.log('🔔 Notification clicked:', notification);
    logger.log('🔔 Message data:', notification.messageData);
    
    // Check if notification has navigation data
    if (!notification.messageData || !notification.messageData.toScreen) {
      logger.log('🔔 No navigation data found, ignoring click');
      return;
    }
    
    const { toScreen } = notification.messageData;
    
    // Handle different screen types
    switch (toScreen) {
      case 'LEAD_PROFILE_SCREEN':
        logger.log('🔔 Navigating to leads page');
        navigate('/leads');
        break;
        
      case 'TASK_HOME_SCREEN':
        logger.log('🔔 Navigating to tasks screen');
        navigate('/tasks');
        break;
        
      default:
        logger.log('🔔 Unknown screen type, no navigation performed:', toScreen);
        break;
    }
  } catch (error) {
    Logger.log('❌ Error handling notification click:', error);
  }
};

/**
 * Get notification summary text for display
 * @param notification - Notification data
 * @returns Truncated body text
 */
export const getNotificationSummary = (notification: NotificationData): string => {
  try {
    // Truncate long notification bodies
    const maxLength = 80;
    if (notification.body.length <= maxLength) {
      return notification.body;
    }
    
    return notification.body.substring(0, maxLength) + '...';
  } catch (error) {
    Logger.log('❌ Error getting notification summary:', error);
    return 'Notification content unavailable';
  }
};

/**
 * Check if notification has a clickable action
 * @param notification - Notification data
 * @returns boolean indicating if notification is clickable
 */
export const isNotificationClickable = (notification: NotificationData): boolean => {
  return !!(
    notification.messageData && 
    notification.messageData.toScreen && 
    notification.messageData.toScreen !== ''
  );
};

// Export default service object with all functions
const userNotificationService = {
  getNotifications,
  getNotificationIcon,
  formatNotificationTime,
  handleNotificationClick,
  getNotificationSummary,
  isNotificationClickable,
};

export default userNotificationService;